import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { CATEGORY_COLORS, CATEGORY_ICONS, SCALE_LABELS } from '../../data/assessment-constants';
import { useAssessmentCtx } from './AssessmentContext';

export default function QuestionsStep() {
  const { answers, currentQuestion, currentQuestionIndex, handleAnswerSelect, handleNext, handlePrev, isCurrentAnswered, navigate, progress, questionsData } = useAssessmentCtx();
  const CategoryIcon = CATEGORY_ICONS[currentQuestion.category] || Target;
  const categoryGradient = CATEGORY_COLORS[currentQuestion.category] || "from-violet-500 to-cyan-600";

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-violet-500/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Header with Progress */}
      <header className="relative z-10 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={() => currentQuestionIndex === 0 ? navigate('/assessment') : handlePrev()}
              className="text-slate-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
              data-testid="assessment-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <span className="text-slate-400 text-sm font-medium">
              {currentQuestionIndex + 1} of {questionsData.questions.length}
            </span>
          </div>
          {/* Progress Bar */}
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Category Badge */}
        <div className="flex justify-center mb-6">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${categoryGradient} bg-opacity-10 border border-white/10`}>
            <CategoryIcon className="h-4 w-4 text-white" />
            <span className="text-white text-sm font-semibold">{currentQuestion.category}</span>
          </div>
        </div>

        {/* Question */}
        <div className="text-center mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-relaxed max-w-2xl mx-auto" data-testid="assessment-question">
            {currentQuestion.question}
          </h2>
        </div>

        {/* Answer Options */}
        <div className="space-y-3 mb-10">
          {SCALE_LABELS.map((option) => {
            const isSelected = answers[currentQuestion.id] === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleAnswerSelect(currentQuestion.id, option.value)}
                data-testid={`answer-option-${option.value}`}
                className={`w-full p-4 rounded-xl border transition-all duration-200 text-left flex items-center gap-4 group ${
                  isSelected 
                    ? 'bg-slate-800 border-cyan-500 shadow-lg shadow-cyan-500/10' 
                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 hover:bg-slate-900'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  isSelected ? `${option.color} text-white` : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700'
                }`}>
                  {option.value}
                </div>
                <span className={`font-medium transition-colors ${isSelected ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>
                  {option.label}
                </span>
                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-cyan-400 ml-auto" />
                )}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-center">
          <Button
            onClick={handleNext}
            disabled={!isCurrentAnswered}
            data-testid="assessment-next-btn"
            size="lg"
            className={`px-8 py-5 font-semibold transition-all duration-300 ${
              isCurrentAnswered
                ? 'bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white shadow-lg shadow-violet-500/25'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed'
            }`}
          >
            {currentQuestionIndex === questionsData.questions.length - 1 ? 'Complete Assessment' : 'Next Question'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </main>
    </div>
  );
}
